/**
 * ShopCell — one item tile in the customizer shop grid (plus the "None" remover).
 * Economy is free for now: no prices, no locks. Footer just reflects equip state.
 */

import { useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Check } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { RARITY, CUSTOMIZER_SURFACE } from './characterCustomizer.constants';
import { FocusTryOn } from './FocusTryOn';
import {
  getCellContainerStyle,
  getFooterTextColorStyle,
  getRarityBadgeFillStyle,
  getShopCellStyles,
} from './ShopCell.styles';
import type { CustomizerCategoryId, CustomizerItem } from './characterCustomizer.types';

interface ShopCellProps {
  category: CustomizerCategoryId;
  /** null renders the "None" tile that clears the category. */
  item: CustomizerItem | null;
  active: boolean;
  onPress: () => void;
}

const THUMB_GRADIENT = [CUSTOMIZER_SURFACE.thumbTop, CUSTOMIZER_SURFACE.thumbBottom] as const;

export function ShopCell({ category, item, active, onPress }: ShopCellProps) {
  const theme = useTheme();
  const styles = useMemo(() => getShopCellStyles(theme), [theme]);
  const rarity = item ? RARITY[item.rarity] : null;

  const containerStyle = getCellContainerStyle(theme, {
    active,
    rarityColor: rarity?.color ?? null,
    glow: rarity?.glow ?? theme.primary,
  });

  const footer = active
    ? { label: 'Equipped', color: theme.secondary, withCheck: true }
    : item
      ? { label: 'Equip', color: theme.primaryLight, withCheck: false }
      : { label: 'Remove', color: theme.gray[400], withCheck: false };

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={item ? `${item.name}: ${footer.label}` : `None: ${footer.label}`}
      style={({ pressed }) => [styles.cell, containerStyle, pressed ? styles.pressed : null]}
    >
      <View style={styles.thumb}>
        <LinearGradient colors={THUMB_GRADIENT} style={styles.thumbGradient} />
        {item ? (
          <FocusTryOn category={category} id={item.id} />
        ) : (
          <View style={styles.offCircle}>
            <Text style={styles.offText}>OFF</Text>
          </View>
        )}
        {rarity ? (
          <View
            style={[styles.rarityDot, getRarityBadgeFillStyle(rarity.color)]}
            accessibilityLabel={`${rarity.label} rarity`}
          />
        ) : null}
      </View>

      <View style={styles.meta}>
        <Text style={styles.name} numberOfLines={1}>
          {item ? item.name : 'None'}
        </Text>
        <View style={styles.footerRow}>
          {footer.withCheck ? <Check size={13} color={footer.color} strokeWidth={3} /> : null}
          <Text style={[styles.footerText, getFooterTextColorStyle(footer.color)]}>
            {footer.label}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}
